import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Container from '../../components/menu/Container';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';
import { Tooltip } from 'primereact/tooltip';
import { useGlobalState } from '../../components/globalState/GlobalStateProvider';
import getFaturaSaudeFinanceiraService, { SaudeFinanceiraDTO } from '../../service/getFaturaSaudeFinanceira/getFaturaSaudeFinanceiraService';
import patchClientStatus from '../../service/patchClientStatus';

// ─── Score helpers ─────────────────────────────────────────────────────────────
const scoreLabel = (s: number) =>
  s >= 80 ? 'Excelente' : s >= 60 ? 'Bom' : s >= 40 ? 'Regular' : 'Crítico';
const scoreGradient = (s: number) =>
  s >= 80
    ? 'linear-gradient(135deg,#16a34a,#22c55e)'
    : s >= 60
    ? 'linear-gradient(135deg,#1d4ed8,#3b82f6)'
    : s >= 40
    ? 'linear-gradient(135deg,#b45309,#f59e0b)'
    : 'linear-gradient(135deg,#b91c1c,#ef4444)';

const parseBRL = (s: string) =>
  parseFloat(s.replace(/[^\d,]/g, '').replace(',', '.')) || 0;

// ─── KPI card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, icon, from, to }: {
  label: string; value: string; sub?: string; icon: string; from: string; to: string;
}) => (
  <div style={{
    background: `linear-gradient(135deg,${from},${to})`,
    borderRadius: 14, padding: '1.1rem 1.25rem', flex: 1, minWidth: 150,
    boxShadow: '0 6px 18px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden',
  }}>
    <div style={{ position: 'absolute', right: -12, bottom: -12, width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{label}</div>
        <div style={{ color: '#fff', fontSize: '1.6rem', fontWeight: 700, lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', marginTop: 4 }}>{sub}</div>}
      </div>
      <div style={{ background: 'rgba(255,255,255,0.18)', borderRadius: 10, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className={icon} style={{ color: '#fff', fontSize: '1.1rem' }} />
      </div>
    </div>
  </div>
);

// ─── Section title ─────────────────────────────────────────────────────────────
const SectionTitle = ({ icon, label }: { icon: string; label: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
    <div style={{ width: 4, height: 20, borderRadius: 2, background: '#4f46e5' }} />
    <i className={icon} style={{ color: '#4f46e5' }} />
    <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem', letterSpacing: '0.02em' }}>{label}</span>
  </div>
);

export default function SaudeFinanceiraCliente() {
  const { idFatura } = useParams<{ idFatura: string }>();
  const { state } = useGlobalState();
  const toast = useRef<Toast>(null);

  const [dados, setDados] = useState<SaudeFinanceiraDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [inativarVisible, setInativarVisible] = useState(false);
  const [inativarLoading, setInativarLoading] = useState(false);

  useEffect(() => {
    if (!idFatura) return;
    setLoading(true);
    getFaturaSaudeFinanceiraService(idFatura)
      .then(setDados)
      .catch(() => toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar a análise', life: 5000 }))
      .finally(() => setLoading(false));
  }, [idFatura]);

  const confirmarInativar = () => {
    if (!dados) return;
    setInativarLoading(true);
    patchClientStatus(dados.clienteId, !dados.clienteInativo)
      .then(() => {
        const novoStatus = !dados.clienteInativo;
        setDados({ ...dados, clienteInativo: novoStatus });
        toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: `Cliente ${novoStatus ? 'inativado' : 'ativado'} com sucesso`, life: 3000 });
        setInativarVisible(false);
      })
      .catch(() => toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao alterar status do cliente', life: 5000 }))
      .finally(() => setInativarLoading(false));
  };

  // ── Highcharts options ───────────────────────────────────────────────────────
  const pieOptions: Highcharts.Options | null = dados ? {
    chart: { type: 'pie', height: 260, backgroundColor: 'transparent' },
    title: { text: '' },
    credits: { enabled: false },
    tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)' },
    plotOptions: {
      pie: {
        innerSize: '60%',
        dataLabels: { enabled: false },
        showInLegend: true,
      },
    },
    legend: { align: 'center', verticalAlign: 'bottom', layout: 'horizontal' },
    series: [{
      type: 'pie',
      name: 'Faturas',
      data: [
        { name: 'Pagas em Dia',     y: dados.quantidadePagoEmDia,                               color: '#22c55e' },
        { name: 'Pagas em Atraso',  y: dados.quantidadePagoEmAtraso,                            color: '#f59e0b' },
        { name: 'Em Aberto',        y: dados.quantidadeRecebivel + dados.quantidadeEmAberto,    color: '#3b82f6' },
        { name: 'Vencidas',         y: dados.quantidadeVencido,                                 color: '#ef4444' },
        { name: 'Incobrável',       y: dados.quantidadeIncobravel,                              color: '#a855f7' },
      ].filter(d => d.y > 0),
    }],
  } : null;

  const columnOptions: Highcharts.Options | null = dados ? {
    chart: { type: 'column', height: 260, backgroundColor: 'transparent' },
    title: { text: '' },
    credits: { enabled: false },
    xAxis: { categories: dados.historico12Meses.map(m => m.mes), crosshair: true },
    yAxis: { title: { text: 'Quantidade' }, allowDecimals: false, min: 0 },
    tooltip: { shared: true },
    plotOptions: { column: { borderRadius: 4, groupPadding: 0.1 } },
    legend: { align: 'center', verticalAlign: 'bottom' },
    series: [
      {
        type: 'column',
        name: 'Pagas',
        data: dados.historico12Meses.map(m => m.quantidadePago),
        color: '#22c55e',
      },
      {
        type: 'column',
        name: 'Em Atraso',
        data: dados.historico12Meses.map(m => m.quantidadeVencido),
        color: '#ef4444',
      },
    ],
  } : null;

  const lineOptions: Highcharts.Options | null = dados ? {
    chart: { type: 'areaspline', height: 260, backgroundColor: 'transparent' },
    title: { text: '' },
    credits: { enabled: false },
    xAxis: { categories: dados.historico12Meses.map(m => m.mes) },
    yAxis: { title: { text: 'Valor (R$)' }, min: 0 },
    tooltip: {
      shared: true,
      valuePrefix: 'R$ ',
      valueDecimals: 2,
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.08,
        marker: { radius: 4 },
        dataLabels: {
          enabled: true,
          style: { fontSize: '0.68rem', fontWeight: '600', textOutline: 'none' },
          formatter: function () {
            return this.y ? 'R$ ' + Highcharts.numberFormat(this.y, 2, ',', '.') : null;
          },
        },
      },
    },
    legend: { align: 'center', verticalAlign: 'bottom' },
    series: [
      {
        type: 'areaspline',
        name: 'Valor Pago',
        data: dados.historico12Meses.map(m => parseBRL(m.valorPago)),
        color: '#22c55e',
        fillColor: 'rgba(34,197,94,0.08)',
      },
      {
        type: 'areaspline',
        name: 'Valor em Atraso',
        data: dados.historico12Meses.map(m => parseBRL(m.valorVencido)),
        color: '#ef4444',
        fillColor: 'rgba(239,68,68,0.05)',
      },
    ],
  } : null;

  const consultasOptions: Highcharts.Options | null = dados?.consultasPorOpcao12Meses?.length ? {
    chart: { type: 'column', height: 320, backgroundColor: 'transparent' },
    title: { text: '' },
    credits: { enabled: false },
    xAxis: { categories: dados.historico12Meses.map(m => m.mes), crosshair: true },
    yAxis: {
      title: { text: 'Consultas' },
      allowDecimals: false,
      min: 0,
      stackLabels: { enabled: true, style: { fontWeight: '700', color: '#334155', textOutline: 'none' } },
    },
    tooltip: { shared: true, pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>' },
    plotOptions: {
      column: {
        stacking: 'normal',
        borderRadius: 3,
        groupPadding: 0.1,
        dataLabels: {
          enabled: true,
          style: { fontSize: '0.65rem', fontWeight: '600', color: '#fff', textOutline: 'none' },
          formatter: function () { return this.y ? this.y : null; },
        },
      },
    },
    legend: { align: 'center', verticalAlign: 'bottom' },
    series: dados.consultasPorOpcao12Meses.map(s => ({
      type: 'column' as const,
      name: s.opcao,
      data: s.quantidades,
    })),
  } : null;

  const statusRowStyle = (row: any) =>
    row.status === 'Incobrável' ? { color: '#a855f7', fontWeight: 600 }
    : row.diasAtraso > 60 ? { color: '#ef4444', fontWeight: 600 }
    : row.diasAtraso > 30 ? { color: '#f59e0b', fontWeight: 600 }
    : { color: '#64748b' };

  if (loading) return (
    <Container>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#4f46e5' }} />
      </div>
    </Container>
  );

  if (!dados) return (
    <Container>
      <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>
        <i className="pi pi-exclamation-circle" style={{ fontSize: '3rem' }} />
        <p style={{ marginTop: '1rem' }}>Não foi possível carregar a análise.</p>
      </div>
    </Container>
  );

  return (
    <Container>
      <Toast ref={toast} />
      <Tooltip target=".score-ring" />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
            <i className="pi pi-chart-bar" style={{ marginRight: 8, color: '#4f46e5' }} />
            Análise Financeira
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '1.05rem', color: '#334155' }}>{dados.clienteNome}</span>
            <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{dados.clienteDocumento}</span>
            <Tag
              value={dados.clienteInativo ? 'Inativo' : 'Ativo'}
              severity={dados.clienteInativo ? 'warning' : 'success'}
              icon={dados.clienteInativo ? 'pi pi-pause-circle' : 'pi pi-check-circle'}
            />
          </div>
        </div>
        {state.user.funcoes.includes('CLIENTE_CADASTRAR') && (
          <Button
            label={dados.clienteInativo ? 'Ativar Cliente' : 'Inativar Cliente'}
            icon={dados.clienteInativo ? 'pi pi-check-circle' : 'pi pi-pause-circle'}
            severity={dados.clienteInativo ? 'success' : 'warning'}
            outlined
            onClick={() => setInativarVisible(true)}
          />
        )}
      </div>

      {/* ── Score + KPIs ── */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem', alignItems: 'stretch' }}>
        <div style={{
          background: scoreGradient(dados.scoreFinanceiro),
          borderRadius: 16, padding: '1.5rem', minWidth: 180,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', gap: 8,
        }}>
          <div
            className="score-ring"
            data-pr-tooltip={`Score: ${dados.scoreFinanceiro}/100`}
            style={{
              width: 80, height: 80, borderRadius: '50%',
              background: `conic-gradient(rgba(255,255,255,0.9) ${dados.scoreFinanceiro * 3.6}deg, rgba(255,255,255,0.2) 0deg)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#fff', fontWeight: 800, fontSize: '1.4rem', lineHeight: 1 }}>{dados.scoreFinanceiro}</span>
            </div>
          </div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>Score Financeiro</div>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '0.15rem 0.75rem', color: '#fff', fontSize: '0.8rem', fontWeight: 600 }}>
            {scoreLabel(dados.scoreFinanceiro)}
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, gap: '0.75rem', flexWrap: 'wrap', minWidth: 0 }}>
          <KpiCard label="Total de Faturas"  value={String(dados.totalFaturas)}               sub={dados.valorTotalFaturas}                                        icon="pi pi-file"                 from="#4f46e5" to="#6366f1" />
          <KpiCard label="Pontualidade"       value={`${dados.percentualPontualidade}%`}        sub={`${dados.quantidadePago} pagas`}                                icon="pi pi-check-circle"         from="#0369a1" to="#0891b2" />
          <KpiCard label="Inadimplência"      value={`${dados.percentualInadimplencia}%`}       sub={`${dados.quantidadeVencido + dados.quantidadeIncobravel} fat.`} icon="pi pi-exclamation-triangle" from="#b91c1c" to="#dc2626" />
          <KpiCard label="Rentabilidade"      value={`${dados.rentabilidade}%`}                 sub={dados.valorTotalPago + ' recebido'}                             icon="pi pi-dollar"               from="#065f46" to="#10b981" />
          <KpiCard label="Média de Atraso"    value={`${dados.mediaAtrasoEmDias} dias`}         sub={dados.quantidadeVencido > 0 ? `${dados.quantidadeVencido} em atraso` : 'Sem atrasos'} icon="pi pi-clock" from="#7c3aed" to="#a855f7" />
        </div>
      </div>

      {/* ── Status breakdown ── */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <SectionTitle icon="pi pi-chart-pie" label="Distribuição de Status" />
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Pagas',      count: dados.quantidadePago,       value: dados.valorTotalPago,       color: '#22c55e' },
            { label: 'A Receber',  count: dados.quantidadeRecebivel,  value: dados.valorTotalRecebivel,  color: '#3b82f6' },
            { label: 'Em Atraso',  count: dados.quantidadeVencido,    value: dados.valorTotalVencido,    color: '#ef4444' },
            { label: 'Incobrável', count: dados.quantidadeIncobravel, value: dados.valorTotalIncobravel, color: '#a855f7' },
            { label: 'Em Aberto',  count: dados.quantidadeEmAberto,   value: '',                        color: '#94a3b8' },
          ].map((item, i) => (
            <div key={i} style={{ flex: 1, minWidth: 120 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500 }}>{item.label}</span>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: item.color }}>{item.count}</span>
              </div>
              <ProgressBar
                value={dados.totalFaturas > 0 ? Math.round((item.count / dados.totalFaturas) * 100) : 0}
                showValue={false}
                style={{ height: 8, borderRadius: 4 }}
                color={item.color}
              />
              {item.value && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 3 }}>{item.value}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* ── Charts row ── */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', flex: '0 0 320px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <SectionTitle icon="pi pi-chart-pie" label="Composição" />
          {pieOptions && <HighchartsReact highcharts={Highcharts} options={pieOptions} />}
        </div>

        <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', flex: 1, minWidth: 300, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <SectionTitle icon="pi pi-chart-bar" label="Faturas — últimos 12 meses" />
          {columnOptions && <HighchartsReact highcharts={Highcharts} options={columnOptions} />}
        </div>
      </div>

      {/* ── Line chart ── */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <SectionTitle icon="pi pi-chart-line" label="Evolução de Valores — últimos 12 meses" />
        {lineOptions && <HighchartsReact highcharts={Highcharts} options={lineOptions} />}
      </div>

      {/* ── Consultas por opção ── */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <SectionTitle icon="pi pi-search" label="Consultas por Opção — últimos 12 meses" />
        {consultasOptions
          ? <HighchartsReact highcharts={Highcharts} options={consultasOptions} />
          : <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Nenhuma consulta nos últimos 12 meses</div>}
      </div>

      {/* ── Histórico de atrasos ── */}
      {dados.historicoAtrasos.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <SectionTitle icon="pi pi-stopwatch" label={`Histórico de Atrasos (${dados.historicoAtrasos.length})`} />
          <DataTable value={dados.historicoAtrasos} size="small" emptyMessage="Nenhum atraso registrado" sortField="diasAtraso" sortOrder={-1}>
            <Column field="numero" header="Fatura" sortable />
            <Column field="dataVencimento" header="Vencimento" sortable />
            <Column field="dataPagamento" header="Pagamento" body={(r) => r.dataPagamento || '—'} />
            <Column field="diasAtraso" header="Dias em Atraso" sortable body={(r) => (
              <span style={statusRowStyle(r)}>{r.diasAtraso > 0 ? `${r.diasAtraso} dias` : '—'}</span>
            )} />
            <Column field="valor" header="Valor" />
            <Column field="status" header="Status" body={(r) => (
              <Tag
                value={r.status}
                severity={r.status === 'Pago em atraso' ? 'warning' : 'danger'}
              />
            )} />
          </DataTable>
        </div>
      )}

      {/* ── Dialog inativar ── */}
      <Dialog
        visible={inativarVisible}
        onHide={() => !inativarLoading && setInativarVisible(false)}
        closable={!inativarLoading}
        showHeader={false}
        style={{ width: '26rem' }}
        contentStyle={{ borderRadius: 14, overflow: 'hidden' }}
      >
        <div style={{ textAlign: 'center', padding: '2rem 1.5rem 1.5rem' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: dados.clienteInativo ? 'linear-gradient(135deg,#065f46,#10b981)' : 'linear-gradient(135deg,#b45309,#f59e0b)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem',
          }}>
            <i className={dados.clienteInativo ? 'pi pi-check-circle' : 'pi pi-pause-circle'} style={{ color: '#fff', fontSize: '1.6rem' }} />
          </div>
          <h3 style={{ margin: '0 0 0.5rem', color: '#1e293b' }}>
            {dados.clienteInativo ? 'Ativar cliente?' : 'Inativar cliente?'}
          </h3>
          <p style={{ color: '#64748b', margin: '0 0 1.5rem', fontSize: '0.9rem' }}>
            {dados.clienteInativo
              ? `O cliente ${dados.clienteNome} será ativado e poderá usar o sistema normalmente.`
              : `O cliente ${dados.clienteNome} será inativado e perderá o acesso ao sistema.`}
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <Button label="Cancelar" severity="secondary" outlined onClick={() => setInativarVisible(false)} disabled={inativarLoading} />
            <Button
              label="Confirmar"
              icon={dados.clienteInativo ? 'pi pi-check' : 'pi pi-pause'}
              severity={dados.clienteInativo ? 'success' : 'warning'}
              onClick={confirmarInativar}
              loading={inativarLoading}
            />
          </div>
        </div>
      </Dialog>
    </Container>
  );
}
